import { Box, Button, useTheme } from "@mui/material";
import { useAtom } from "jotai";
import { PlusIcon } from "lucide-react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PortfolioCategorySection } from "web_myplann/Portfolio/Components/Redesign/PortfolioCategorySection";
import { PortfolioEmptyState } from "web_myplann/Portfolio/Components/Redesign/PortfolioEmptyState";
import { PortfolioFilterChip } from "web_myplann/Portfolio/Components/Redesign/PortfolioFilterChip";
import { PortfolioListSkeleton } from "web_myplann/Portfolio/Components/Redesign/PortfolioListSkeleton";
import { isPortfolioListNarrowed } from "web_myplann/Portfolio/Helpers/isPortfolioListNarrowed";
import { usePortfolioListData } from "web_myplann/Portfolio/Hooks/usePortfolioListData";
import type { PortfolioCategoryId } from "web_myplann/Portfolio/Types/portfolioCategory";
import { ListCountBarRedesign } from "web_myplann/Shared/Components/Redesign/ListCountBarRedesign";
import { RedesignListBackground } from "web_myplann/Shared/Components/Redesign/RedesignListBackground";
import { portfolioCollapsedSectionsState } from "web_myplann/Shared/State/portfolioCollapsedSectionsAtom";

type Props = {
	hasPortfolioRight: boolean;
};

export const PortfolioListRedesign: FC<Props> = ({ hasPortfolioRight }) => {
	const { t } = useResource();
	const theme = useTheme();
	const navigate = useNavigate();

	const { categories, filter, totalItems, summaryData, isLoading, hasNoData } =
		usePortfolioListData();
	const showItemsCount = isPortfolioListNarrowed(filter);

	const [collapsed, setCollapsed] = useAtom(portfolioCollapsedSectionsState);

	const toggleGroup = (id: PortfolioCategoryId) => {
		setCollapsed((prev) =>
			prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
		);
	};

	if (isLoading) {
		return <PortfolioListSkeleton />;
	}

	if (hasNoData) {
		return <PortfolioEmptyState hasPortfolioRight={hasPortfolioRight} />;
	}

	return (
		<RedesignListBackground data-testid="portfolio-list-page-redesign">
			<ListCountBarRedesign
				count={totalItems}
				labelKey={
					showItemsCount ? Resources.Common.ListBar.itemsCount : undefined
				}
				action={<PortfolioFilterChip />}
			/>

			{categories.map((category) => (
				<PortfolioCategorySection
					key={category.id}
					category={category}
					collapsed={collapsed.includes(category.id)}
					onToggle={() => toggleGroup(category.id)}
					summaryData={summaryData}
				/>
			))}

			{hasPortfolioRight && totalItems > 0 && (
				<Box px={theme.spacingTokens.lg} mt={theme.spacingTokens.xl}>
					<Button
						variant="secondary"
						startIcon={<PlusIcon size={theme.iconSize.md} />}
						onClick={() => navigate(Routing.Portfolio.PortfolioAddItem.path())}
						sx={{ color: theme.palette.blue[600] }}
					>
						{t(Resources.Portfolio.List.Redesign.addItem)}
					</Button>
				</Box>
			)}
		</RedesignListBackground>
	);
};
